import {Box, Tooltip} from "@mui/material";
import {getIconForLanguage, getLanguageLabel, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import React, { useState } from "react";

interface Props {
    languageCodes: string[] | undefined;
    asPrint?: boolean;
}

const getLanguageItemLabel = (languageCode: string) =>
    getLanguageLabel(languageCode, getNameForLanguage) || languageCode;

function LanguageItem({ languageCode }: { languageCode: string }) {
    const [hasError, setHasError] = useState(false);
    const languageIcon = getIconForLanguage(languageCode);
    const label = getLanguageItemLabel(languageCode);

    if (!languageIcon || hasError) {
        return (
            <Tooltip title={label} arrow>
                <Box
                    component="span"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.93em", // ~13px; scales with the row's responsive base
                        fontWeight: 500,
                        lineHeight: "18px",
                        whiteSpace: "nowrap",
                    }}
                >
                    <HelpOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    {label}
                </Box>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={label} arrow>
            <Box
                component="img"
                alt={label}
                src={languageIcon}
                onError={() => setHasError(true)}
                sx={{
                    display: "block",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    objectFit: "cover",
                }}
            />
        </Tooltip>
    );
}

export function LanguagesElement({ languageCodes, asPrint = false }: Props) {
    if (!languageCodes?.length) {
        return null;
    }

    const uniqueByNormalizedCode = new Map<string, string>();
    languageCodes.forEach(languageCode => {
        const trimmedCode = languageCode?.trim();
        const normalizedCode = trimmedCode?.toLowerCase();
        if (trimmedCode && normalizedCode && !uniqueByNormalizedCode.has(normalizedCode)) {
            uniqueByNormalizedCode.set(normalizedCode, trimmedCode);
        }
    });
    const uniqueLanguageCodes = Array.from(uniqueByNormalizedCode.values())
        .sort((a, b) => a.localeCompare(b));

    if (!uniqueLanguageCodes.length) {
        return null;
    }

    if (asPrint) {
        return <>{uniqueLanguageCodes.join(', ')}</>;
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                minWidth: 0,
            }}
        >
            {uniqueLanguageCodes.map(languageCode => (
                <LanguageItem key={languageCode} languageCode={languageCode} />
            ))}
        </Box>
    );
}
